// ThemeSettingPanel.tsx
// テーマ詳細設定パネル（カラーピッカー、アイコン選択）

import React from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Avatar,
} from '@mui/material';
import { ThemeType } from '../data/ThemeType';
import { iconMap, iconOptions } from '../../HomePage/Components/ApplicationSelect/iconMap';

// バリデーションエラーのインターフェース
interface ValidationErrors {
  themeName?: string;
}

interface ThemeSettingPanelProps {
  theme: ThemeType;
  onChange: (theme: Omit<ThemeType, 'id'>) => void;
  validationErrors?: ValidationErrors;
}

/**
 * テーマ詳細設定パネルコンポーネント
 * - テーマ名・説明・アイコン選択・カラーピッカーによる色設定を提供する
 * - props: theme (現在のテーマ設定値), onChange (設定変更コールバック),
 *          validationErrors (バリデーションエラー)
 */
export default function ThemeSettingPanel({ theme, onChange, validationErrors = {} }: ThemeSettingPanelProps) {
  // カラー変更ハンドラー
  const handleColorChange = (field: keyof ThemeType, value: string) => {
    onChange({ ...theme, [field]: value });
  };

  // アイコン選択変更
  const handleIconChange = (iconName: string) => {
    onChange({ ...theme, iconName });
  };

  return (
    <Box>
      {/* テーマ名 */}
      <TextField
        fullWidth
        label="テーマ名"
        required
        value={theme.themeName}
        onChange={(e) => handleColorChange('themeName', e.target.value)}
        error={!!validationErrors.themeName}
        helperText={validationErrors.themeName}
        sx={{
          mb: 2,
          '& .MuiFormHelperText-root': {
            fontSize: '1rem'
          }
        }}
      />

      {/* テーマ説明 */}
      <TextField
        fullWidth
        label="テーマ説明"
        multiline
        rows={2}
        value={theme.themeDescription || ''}
        onChange={(e) => handleColorChange('themeDescription', e.target.value)}
        sx={{ mb: 3 }}
      />

      {/* アイコン設定セクション */}
      <Paper variant="outlined" sx={{ p:2,pt:1, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" mb={1} fontWeight="bold">
          アイコン設定
        </Typography>

        {/* アイコン選択 */}
        <FormControl fullWidth sx={{ mb: 2 }} required>
          <InputLabel required>アイコン</InputLabel>
          <Select
            value={theme.iconName}
            label="アイコン"
            onChange={(e) => handleIconChange(e.target.value)}
            renderValue={(value) => {
              const selected = iconOptions.find((opt) => opt.name === value);
              const IconComponent = iconMap[value] || iconMap['MoreHorizRoundedIcon'];
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 24, height: 24, bgcolor: 'grey.500' }}>
                    <IconComponent style={{ width: 16, height: 16 }} />
                  </Avatar>
                  <span>{selected?.label || value}</span>
                </Box>
              );
            }}
          >
            {iconOptions.map((option) => {
              const IconComponent = iconMap[option.name] || iconMap['MoreHorizRoundedIcon'];
              return (
                <MenuItem key={option.name} value={option.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'grey.500' }}>
                      <IconComponent style={{ width: 16, height: 16 }} />
                    </Avatar>
                    <span>{option.label}</span>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {/* アイコンカラー設定 */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box>
              <Typography variant="button" display="block" gutterBottom>
                アイコン色
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="color"
                  value={theme.iconColor}
                  onChange={(e) => handleColorChange('iconColor', e.target.value)}
                  style={{
                    width: '40px',
                    height: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                />
                <TextField
                  size="small"
                  value={theme.iconColor}
                  onChange={(e) => handleColorChange('iconColor', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Box>
              <Typography variant="button" display="block" gutterBottom>
                アイコン背景色
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="color"
                  value={theme.iconBgColor}
                  onChange={(e) => handleColorChange('iconBgColor', e.target.value)}
                  style={{
                    width: '40px',
                    height: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                />
                <TextField
                  size="small"
                  value={theme.iconBgColor}
                  onChange={(e) => handleColorChange('iconBgColor', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Box>
              <Typography variant="button" display="block" gutterBottom>
                アイコン枠線色
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="color"
                  value={theme.iconBdColor || '#ffffff'}
                  onChange={(e) => handleColorChange('iconBdColor', e.target.value)}
                  style={{
                    width: '40px',
                    height: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                />
                <TextField
                  size="small"
                  value={theme.iconBdColor || ''}
                  onChange={(e) => handleColorChange('iconBdColor', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* ボタン設定セクション */}
      <Paper variant="outlined" sx={{p:2,pt:1, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          ボタン設定
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box>
              <Typography variant="button" display="block" gutterBottom>
                ボタン背景色
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="color"
                  value={theme.buttonColor}
                  onChange={(e) => handleColorChange('buttonColor', e.target.value)}
                  style={{
                    width: '40px',
                    height: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                />
                <TextField
                  size="small"
                  value={theme.buttonColor}
                  onChange={(e) => handleColorChange('buttonColor', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Box>
              <Typography variant="button" display="block" gutterBottom>
                ボタン文字色
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="color"
                  value={theme.fontColor}
                  onChange={(e) => handleColorChange('fontColor', e.target.value)}
                  style={{
                    width: '40px',
                    height: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                />
                <TextField
                  size="small"
                  value={theme.fontColor}
                  onChange={(e) => handleColorChange('fontColor', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Box>
              <Typography variant="button" display="block" gutterBottom>
                ボタン枠線色
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="color"
                  value={theme.buttonBdColor || '#ffffff'}
                  onChange={(e) => handleColorChange('buttonBdColor', e.target.value)}
                  style={{
                    width: '40px',
                    height: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                />
                <TextField
                  size="small"
                  value={theme.buttonBdColor || ''}
                  onChange={(e) => handleColorChange('buttonBdColor', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
